import type { EvaluationRun } from '../../services/evaluationRun.interface.js';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export function getEvalRunContext(run: EvaluationRun) {
  const config = isRecord(run.resolvedConfig)
    ? run.resolvedConfig.value
    : undefined;
  const metadata = isRecord(config) ? config.metadata : undefined;
  const tags = isRecord(metadata) ? metadata.tags : undefined;
  const invalidTags =
    (metadata != null && !isRecord(metadata)) ||
    (tags != null &&
      (!isRecord(tags) ||
        Object.values(tags).some((value) => typeof value !== 'string')));
  const tag = (key: string) => (isRecord(tags) ? text(tags[key]) : undefined);
  const ci = run.executionContext?.ciContext;
  return {
    pipelineName: text(ci?.pipelineName) ?? tag('ciPipeline'),
    branch: text(ci?.branch) ?? tag('ciSourceBranch') ?? tag('branch'),
    buildId: text(ci?.buildId) ?? tag('ciRunId'),
    project: text(ci?.project),
    system: text(ci?.system),
    buildUrl: text(ci?.buildUrl),
    invalidTags,
  };
}

export interface RunContextFilters {
  pipeline?: string;
  branch?: string;
  buildId?: string;
  project?: string;
  ciSystem?: string;
}

export function getRunContextSuggestions(runs: EvaluationRun[]) {
  const contexts = runs.map(getEvalRunContext);
  const values = (
    field: 'pipelineName' | 'branch' | 'buildId' | 'project' | 'system',
  ) =>
    [
      ...new Set(
        contexts
          .map((context) => context[field])
          .filter((value): value is string => value !== undefined),
      ),
    ].sort((a, b) => a.localeCompare(b));
  return {
    pipeline: values('pipelineName'),
    branch: values('branch'),
    build: values('buildId'),
    project: values('project'),
    ci: values('system'),
  };
}

export function matchesRunContext(
  run: EvaluationRun,
  filters: RunContextFilters,
) {
  const ci = getEvalRunContext(run);
  return (
    (!filters.ciSystem || ci.system === filters.ciSystem) &&
    (!filters.buildId?.trim() || ci.buildId === filters.buildId.trim()) &&
    [
      [ci.pipelineName, filters.pipeline],
      [ci.branch, filters.branch],
      [ci.project, filters.project],
    ].every(
      ([value, filter]) =>
        !filter?.trim() || Boolean(value?.includes(filter.trim())),
    )
  );
}
